/**
 * StatusBadge — maps job status codes to UI-SPEC color tokens.
 *
 * Phase 9 / Plan 09-04 — Implements the status badge color mapping from
 * UI-SPEC §Interaction Contracts. Uses shadcn Badge + CSS custom properties.
 *
 * Color rules (UI-SPEC § Interaction Contracts):
 *   PENDING                   → warning (amber)
 *   EN_ROUTE_* / ARRIVED_PICKUP / ONSITE_* → accent-blue
 *   DISPOSAL_* / UNLOADING_START → warning (amber)
 *   COMPLETED                 → success (green)
 *   IN_REVIEW                 → danger (red)
 *   TO_BE_INVOICED / INVOICED → neutral (text-secondary)
 *   CANCELLED                 → muted (text-tertiary)
 *   PAUSED                    → warning (amber) — add pause icon visual hint
 *   REJECTED                  → danger (red)
 *   Unknown                   → neutral (text-secondary)
 *
 * Typography: 12px, uppercase, tracking-wide (UI-SPEC Typography — Label/Mono slot).
 *
 * Spec: UI-SPEC §Interaction Contracts (status badge color mapping); DISPATCH-01.
 */

import * as React from 'react';
import {
  getStatusVariant,
  statusAbbrLabel,
  statusFullLabel,
  STATUS_VARIANT_COLORS,
} from './job-status.js';

// Quick 260615-qgd (#2) — variant→color now lives in job-status.ts
// (STATUS_VARIANT_COLORS) so badges + the queue group headers share one palette,
// including the expanded orange/teal/yellow set.

interface StatusBadgeProps {
  /** The job status code (e.g. 'PENDING', 'REJECTED', 'COMPLETED'). */
  readonly statusCode: string;
  /**
   * Render the full label instead of the abbreviation. Default false (dense
   * contexts — job queue STATUS column, driver cards — use the abbreviation +
   * a native tooltip with the full label so text never truncates; #1).
   */
  readonly full?: boolean;
}

/**
 * Inline status badge with color-coded variant per UI-SPEC § Interaction Contracts.
 *
 * Typography: 12px mono uppercase tracking-wide (Label/Mono slot — UI-SPEC Typography).
 * The badge shows the abbreviated label and carries the full label as a `title`
 * tooltip (Quick 260614-s5w — fixes STATUS column truncation).
 */
export function StatusBadge({ statusCode, full = false }: StatusBadgeProps): React.JSX.Element {
  const variant = getStatusVariant(statusCode);
  const c = STATUS_VARIANT_COLORS[variant];
  const fullLabel = statusFullLabel(statusCode);

  return (
    <span
      data-status-code={statusCode}
      data-status-variant={variant}
      title={fullLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: '4px',
        border: '1px solid',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-mono, monospace)',
        backgroundColor: c.bg,
        color: c.fg,
        borderColor: c.border,
      }}
    >
      {full ? fullLabel : statusAbbrLabel(statusCode)}
    </span>
  );
}
